import React from "react";
import {
  useBooleanState,
  useDisclosure,
  UseDisclosureReturn
} from "@gettactic/helpers";
import { useUserListPaginated } from "@gettactic/hooks";
import { Category } from "@v2/components/form/MentionTextarea/types";
import { Users } from "lucide-react";
import { BooleanState } from "@gettactic/helpers/src";
import { useGetTessaUserChatList } from "@gettactic/hooks";
import { TessaChatListsResponse } from "@gettactic/api";
import { useLocalStorageHandler } from "@gettactic/helpers/src/storage/useLocalStorageHandler";
import { useChat } from "@ai-sdk/react";
interface NewChatItem {
  id: string;
  title: string;
}

export type TessaTargetType =
  | "user"
  | "resource"
  | "floor"
  | "workplace-request";

export interface TessaTarget {
  type: TessaTargetType;
  id: string;
  label: string;
}

export interface TessaDisclosureOptions {
  targets?: TessaTarget[];
  initialPrompt?: string;
}

export interface FullPageToolDisclosureOptions {
  partId: string;
}

export interface AskTessaOptions {
  tessaDisclosure: UseDisclosureReturn<TessaDisclosureOptions>;
  sidebarDisclosure: UseDisclosureReturn;
  fullPageToolDisclosure: UseDisclosureReturn<FullPageToolDisclosureOptions>;
  autoOpenTool?: string | null;
  suggestions: Category[];
  isPopup: BooleanState;
  isDocked: BooleanState;
  dockedSide: "left" | "right" | null;
  selectedChatId: string | null | undefined;
  chats: TessaChatListsResponse | null | undefined;
  newlyCreatedChatIds: Set<string>;
  isNewChat: boolean;
  newChatItem: NewChatItem | null;
}

export interface AskTessaActions {
  open: (options?: TessaDisclosureOptions) => void;
  setDockedSide: (side: "left" | "right" | null) => void;
  startNewChat: (chat?: ReturnType<typeof useChat>, text?: string) => void;
  setSelectedChatId: (chatId: string | null | undefined) => void;
  setAutoOpenTool: (toolId: string | null) => void;
  markChatAsNew: (chatId: string) => void;
  markChatAsPersisted: (chatId: string) => void;
  sendMessageRef: React.MutableRefObject<((message: string) => void) | null>;
  setNewChatItem: (item: NewChatItem | null) => void;
}

export type AskTessaState = AskTessaOptions & AskTessaActions;

export const AskTessaContext = React.createContext<AskTessaState | null>(null);

export const AskTessaProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const { chats } = useGetTessaUserChatList();
  const localStorage = useLocalStorageHandler("asktessa");

  const initialIsDocked = localStorage.get<boolean>("isDocked", false);
  const initialDockedSide = localStorage.get<"left" | "right" | null>(
    "dockedSide",
    null
  );
  const initialIsPopup = localStorage.get<boolean>("isPopup", false);
  const initialSelectedChatId = ((): string | null | undefined => {
    const value = "";
    const got = localStorage.get<string | null | "">(
      "selectedChatId",
      value as ""
    );
    return got === value ? undefined : got;
  })();
  const initialSidebarOpen = localStorage.get<boolean>("sidebarOpen", false);

  const [selectedChatId, setSelectedChatIdState] = React.useState<
    string | null | undefined
  >(initialSelectedChatId);
  const [autoOpenTool, setAutoOpenTool] = React.useState<string | null>(null);
  const [newlyCreatedChatIds, setNewlyCreatedChatIds] = React.useState<
    Set<string>
  >(new Set());
  const pendingNewChatMessageRef = React.useRef<{
    chatInstance: any;
    text: string;
  } | null>(null);
  const sendMessageRef = React.useRef<((message: string) => void) | null>(null);
  const isPopup = useBooleanState(initialIsPopup);
  const tessaDisclosure = useDisclosure<TessaDisclosureOptions>();
  const sidebarDisclosure = useDisclosure();
  const fullPageToolDisclosure = useDisclosure<FullPageToolDisclosureOptions>();
  const isDocked = useBooleanState(initialIsDocked);
  const { data: users } = useUserListPaginated("", 300);
  const [dockedSide, setDockedSideState] = React.useState<
    "left" | "right" | null
  >(initialDockedSide);

  const [newChatItem, setNewChatItem] = React.useState<NewChatItem | null>(
    null
  );

  React.useEffect(() => {
    const shouldOpen = initialIsPopup && initialIsDocked;
    //tessaDisclosure.setOpen(shouldOpen);
    sidebarDisclosure.setOpen(initialSidebarOpen);
  }, []);

  const isNewChat = React.useMemo(() => {
    return (
      selectedChatId === "new" ||
      (selectedChatId ? newlyCreatedChatIds.has(selectedChatId) : false)
    );
  }, [selectedChatId, newlyCreatedChatIds]);

  React.useEffect(() => {
    // send new chat message only when next chat id is defined
    // TBD move useTessaChat into components and tight sending message to new chat creation
    if (
      pendingNewChatMessageRef.current &&
      selectedChatId &&
      selectedChatId !== "new"
    ) {
      const { chatInstance, text } = pendingNewChatMessageRef.current;
      chatInstance.sendMessage({ text });
      pendingNewChatMessageRef.current = null;
    }
  }, [selectedChatId]);

  React.useEffect(() => {
    localStorage.set("isDocked", isDocked.state);
  }, [isDocked.state, localStorage]);
  React.useEffect(() => {
    localStorage.set("dockedSide", dockedSide);
    if (dockedSide) {
      localStorage.set("isDocked", true);
    } else {
      localStorage.set("isDocked", false);
    }
  }, [dockedSide, localStorage]);
  React.useEffect(() => {
    if (typeof selectedChatId !== "undefined")
      localStorage.set("selectedChatId", selectedChatId);
  }, [selectedChatId, localStorage]);
  React.useEffect(() => {
    localStorage.set("sidebarOpen", sidebarDisclosure.isOpen);
  }, [sidebarDisclosure.isOpen, localStorage]);
  React.useEffect(() => {
    localStorage.set("isPopup", isPopup.state);
  }, [isPopup.state, localStorage]);

  const suggestions = React.useMemo(() => {
    const suggestions: Category[] = [];
    const usersCategory: Category = {
      name: "Users",
      icon: Users,
      suggestions: []
    };
    (users?.pages?.[0]?.elements || []).forEach((user) => {
      usersCategory.suggestions.push({
        id: user.email,
        label: user.name,
        value: `@${user.name}`,
        category: "Users"
      });
    });
    suggestions.push(usersCategory);
    return suggestions;
  }, [users]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;
      const isA = event.key.toLowerCase() === "a";
      if (isCmdOrCtrl && isShift && isA) {
        event.preventDefault();
        tessaDisclosure.toggle();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [tessaDisclosure]);

  const setDockedSide = React.useCallback((side: "left" | "right" | null) => {
    setDockedSideState(side);
  }, []);

  const setSelectedChatId = (chatId: string | null | undefined) => {
    setSelectedChatIdState(chatId);
  };

  const markChatAsNew = React.useCallback((chatId: string) => {
    setNewlyCreatedChatIds((prev) => new Set(prev).add(chatId));
  }, []);

  const markChatAsPersisted = React.useCallback((chatId: string) => {
    setNewlyCreatedChatIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
  }, []);

  const queueMessageForNewChat = React.useCallback(
    (chatInstance: any, text: string) => {
      pendingNewChatMessageRef.current = { chatInstance, text: text.trim() };
    },
    []
  );

  return (
    <AskTessaContext.Provider
      value={{
        tessaDisclosure,
        open: tessaDisclosure.open,
        suggestions,
        isPopup,
        isDocked,
        sidebarDisclosure,
        dockedSide,
        setDockedSide,
        fullPageToolDisclosure,
        selectedChatId,
        setSelectedChatId,
        startNewChat: (chat, text) => {
          setSelectedChatId(chat?.id || "new");
          setNewChatItem(null);
          if (text) {
            queueMessageForNewChat(chat, text);
            setNewChatItem({
              id: chat?.id || "new",
              title: String(text || "").slice(0, 20)
            });
          }
          fullPageToolDisclosure.close();
        },
        chats,
        autoOpenTool,
        setAutoOpenTool,
        newlyCreatedChatIds,
        markChatAsNew,
        markChatAsPersisted,
        isNewChat,
        sendMessageRef,
        newChatItem,
        setNewChatItem
      }}
    >
      {children}
    </AskTessaContext.Provider>
  );
};

export const useAskTessa = () => {
  const context = React.useContext(AskTessaContext);
  return context;
};
